/* ==========================================================================
   SKIMS-layout PDP behaviors — scuffers-pdp.js (wholesale replacement, 2026-07-03)
   Specs: research/BEHAVIORS-skims-pdp.md + skims-pdp-*.spec.md

   - Gallery: horizontal scroll-snap swiper (no external Swiper), desktop
     pointer-drag with momentum (free-mode feel), overlay arrows, custom 8px
     drag-scrollbar, click-zoom modal (Escape / backdrop closes).
   - Variant state machine (SKIMS): size UNSELECTED at load -> ATB disabled
     "Select a Size"; picking a size enables ATB "Add to Bag". Owns
     window.currentVariantId + #product-variant-id. Uses NEW selectors
     (.skpdp-size-btn/.skpdp-swatch) so theme.js legacy bindings stay dormant.
   - Main ATB submit is handled by theme.js's form[action="/cart/add"] AJAX
     handler (posts /cart/add.js + opens the Scuffers cart drawer — deliberate
     hybrid). This file never double-binds the main form.
   - Tabs: instant [hidden] toggle between pre-rendered panels.
   - Complete the Look: custom listbox size picker + /cart/add.js + SadyCart.
   - Similar Styles: recommendations fetched via data-url (standard Shopify
     recommendations endpoint), page-scroll carousel with 1/N counter.
   - CONVERSION KIT (2026-07-03): low-stock line driven off variant
     inventory_quantity/inventory_management inside updateUI; mobile sticky
     ATC reinstated (SKIMS lacks one, conversion data wins) using the
     Scuffers dual-visibility pattern (product-sticky-atc.spec.md):
     is-before-form / is-after-form / footer force-hide, size sheet over a
     blurred backdrop. Sticky mirrors the real ATB state via kitHooks.
   ========================================================================== */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function moneyFormat(cents) {
    if (cents == null) return '';
    var symbol = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'USD') ? '$' : '€';
    var amount = (cents / 100).toFixed(2);
    return symbol + amount.replace(/\.00$/, '');
  }

  ready(function () {
    var root = document.querySelector('.skpdp');
    if (!root) return;

    /* ================================================== 1. GALLERY */
    var gallery = root.querySelector('[data-skpdp-gallery]');
    var galleryWrap = gallery ? gallery.closest('.skpdp-gallery-wrap') : null;
    var suppressClick = false;

    if (gallery) {
      var slides = gallery.querySelectorAll('.skpdp-slide');
      /* colorway filter hides slides via display:none — every nav computation
         below must work in the "visible slides" world, not raw indices */
      var visibleSlides = function () {
        return Array.prototype.filter.call(slides, function (s) { return s.style.display !== 'none'; });
      };
      var scrollbar = root.querySelector('[data-skpdp-scrollbar]');
      var drag = root.querySelector('[data-skpdp-scrollbar-drag]');

      var slideStep = function () {
        var vis = visibleSlides();
        if (vis.length < 2) return gallery.clientWidth;
        return vis[1].offsetLeft - vis[0].offsetLeft; // width + gap
      };

      /* ----- custom scrollbar sync ----- */
      var syncScrollbar = function () {
        if (!scrollbar || !drag) return;
        var max = gallery.scrollWidth - gallery.clientWidth;
        var trackW = scrollbar.clientWidth;
        var dragW = Math.max(24, trackW * gallery.clientWidth / gallery.scrollWidth);
        var x = max > 0 ? (gallery.scrollLeft / max) * (trackW - dragW) : 0;
        drag.style.width = dragW + 'px';
        drag.style.transform = 'translateX(' + x + 'px)';
      };
      gallery.addEventListener('scroll', syncScrollbar, { passive: true });
      window.addEventListener('resize', syncScrollbar);
      syncScrollbar();

      /* ----- scrollbar thumb drag / track click ----- */
      if (scrollbar && drag) {
        var sbDrag = null;
        drag.addEventListener('pointerdown', function (e) {
          e.preventDefault();
          e.stopPropagation();
          sbDrag = { startX: e.clientX, startScroll: gallery.scrollLeft };
          drag.setPointerCapture(e.pointerId);
        });
        drag.addEventListener('pointermove', function (e) {
          if (!sbDrag) return;
          var max = gallery.scrollWidth - gallery.clientWidth;
          var trackW = scrollbar.clientWidth - drag.offsetWidth;
          if (trackW <= 0) return;
          gallery.scrollLeft = sbDrag.startScroll + (e.clientX - sbDrag.startX) * (max / trackW);
        });
        var endSbDrag = function () { sbDrag = null; };
        drag.addEventListener('pointerup', endSbDrag);
        drag.addEventListener('pointercancel', endSbDrag);
        scrollbar.addEventListener('click', function (e) {
          if (e.target === drag) return;
          var rect = scrollbar.getBoundingClientRect();
          var ratio = (e.clientX - rect.left) / rect.width;
          gallery.scrollTo({ left: ratio * (gallery.scrollWidth - gallery.clientWidth), behavior: 'smooth' });
        });
      }

      /* ----- desktop pointer drag with momentum (free-mode feel) ----- */
      var pd = null;
      var momentumRaf = null;
      gallery.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        cancelGalAnim(); /* user grabs the gallery — abort any arrow animation */
        if (momentumRaf) { cancelAnimationFrame(momentumRaf); momentumRaf = null; }
        pd = { startX: e.clientX, startScroll: gallery.scrollLeft, lastX: e.clientX, lastT: performance.now(), v: 0, moved: false };
      });
      gallery.addEventListener('pointermove', function (e) {
        if (!pd) return;
        var dx = e.clientX - pd.startX;
        if (!pd.moved && Math.abs(dx) > 4) {
          pd.moved = true;
          gallery.classList.add('is-dragging');
          gallery.setPointerCapture(e.pointerId);
        }
        if (pd.moved) {
          var now = performance.now();
          var dt = now - pd.lastT;
          if (dt > 0) pd.v = (pd.lastX - e.clientX) / dt; // px per ms
          pd.lastX = e.clientX;
          pd.lastT = now;
          gallery.scrollLeft = pd.startScroll - dx;
        }
      });
      var endDrag = function () {
        if (!pd) return;
        var wasMoved = pd.moved;
        var v = pd.v;
        pd = null;
        if (wasMoved) {
          suppressClick = true;
          setTimeout(function () { suppressClick = false; }, 60);
          // momentum decay, then release snap
          var step = function () {
            v *= 0.94;
            if (Math.abs(v) < 0.05) {
              gallery.classList.remove('is-dragging');
              momentumRaf = null;
              return;
            }
            gallery.scrollLeft += v * 16;
            momentumRaf = requestAnimationFrame(step);
          };
          momentumRaf = requestAnimationFrame(step);
        } else {
          gallery.classList.remove('is-dragging');
        }
      };
      gallery.addEventListener('pointerup', endDrag);
      gallery.addEventListener('pointercancel', endDrag);
      gallery.addEventListener('dragstart', function (e) { e.preventDefault(); });

      /* ----- programmatic slide navigation (owned rAF animation) -----
         FIX2 2026-07-04: scrollBy({behavior:'smooth'}) + scroll-snap is
         unreliable in WebKit/Blink — the snap controller (and our drag
         momentum rAF, which keeps assigning scrollLeft for up to ~1s after
         a drag) cancels or rewinds the native smooth animation, so arrow
         clicks appeared dead / snapped back to the same slide. We animate
         scrollLeft ourselves with snap temporarily disabled, then restore
         it while parked exactly on a snap offset. */
      var galAnim = null;
      var cancelGalAnim = function () {
        if (galAnim) {
          cancelAnimationFrame(galAnim.raf);
          galAnim = null;
          gallery.style.scrollSnapType = '';
        }
      };
      var animateGalleryTo = function (left) {
        if (momentumRaf) { cancelAnimationFrame(momentumRaf); momentumRaf = null; }
        gallery.classList.remove('is-dragging');
        var max = gallery.scrollWidth - gallery.clientWidth;
        left = Math.max(0, Math.min(max, left));
        if (galAnim) cancelAnimationFrame(galAnim.raf);
        var from = gallery.scrollLeft;
        if (Math.abs(left - from) < 1) {
          galAnim = null;
          gallery.style.scrollSnapType = '';
          return;
        }
        gallery.style.scrollSnapType = 'none';
        var dur = 340;
        var t0 = performance.now();
        var anim = { target: left, raf: 0 };
        var tick = function (now) {
          if (galAnim !== anim) return;
          var t = Math.min(1, (now - t0) / dur);
          var e = 1 - Math.pow(1 - t, 3); /* easeOutCubic */
          gallery.scrollLeft = from + (left - from) * e;
          if (t < 1) {
            anim.raf = requestAnimationFrame(tick);
          } else {
            galAnim = null;
            gallery.style.scrollSnapType = '';
          }
        };
        anim.raf = requestAnimationFrame(tick);
        galAnim = anim;
      };
      var slideOffset = function (index) {
        var vis = visibleSlides();
        return vis[index] ? vis[index].offsetLeft - vis[0].offsetLeft : index * slideStep();
      };
      var currentSlideIndex = function () {
        var left = galAnim ? galAnim.target : gallery.scrollLeft;
        return Math.round(left / slideStep());
      };
      var goToSlide = function (index) {
        index = Math.max(0, Math.min(visibleSlides().length - 1, index));
        animateGalleryTo(slideOffset(index));
      };

      /* ----- overlay arrows — advance exactly one slide per click ----- */
      var prevBtn = root.querySelector('[data-skpdp-prev]');
      var nextBtn = root.querySelector('[data-skpdp-next]');
      if (prevBtn) prevBtn.addEventListener('click', function () {
        goToSlide(currentSlideIndex() - 1);
      });
      if (nextBtn) nextBtn.addEventListener('click', function () {
        goToSlide(currentSlideIndex() + 1);
      });

      /* ----- zoom modal ----- */
      var zoom = document.querySelector('[data-skpdp-zoom]');
      var openZoom = function (index) {
        if (!zoom) return;
        zoom.hidden = false;
        document.body.classList.add('no-scroll');
        // kick every lazy image immediately — the user is about to scroll
        // through all of them and lazy-in-a-modal loads too late
        zoom.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
          img.loading = 'eager';
        });
        if (index != null) {
          var target = zoom.querySelector('[data-zoom-index="' + index + '"]');
          if (target) target.scrollIntoView({ block: 'start' });
        }
      };
      var closeZoom = function () {
        if (!zoom) return;
        zoom.hidden = true;
        document.body.classList.remove('no-scroll');
      };
      var zoomBtn = root.querySelector('[data-skpdp-zoom-open]');
      if (zoomBtn) zoomBtn.addEventListener('click', function () {
        var vis = visibleSlides();
        var ord = Math.max(0, Math.min(vis.length - 1, Math.round(gallery.scrollLeft / slideStep())));
        openZoom(vis[ord] ? vis[ord].getAttribute('data-slide-index') : 0);
      });
      gallery.addEventListener('click', function (e) {
        if (suppressClick) return;
        var slide = e.target.closest('.skpdp-slide');
        if (slide) openZoom(slide.getAttribute('data-slide-index'));
      });
      if (zoom) {
        zoom.querySelectorAll('[data-skpdp-zoom-close]').forEach(function (el) {
          el.addEventListener('click', closeZoom);
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && !zoom.hidden) closeZoom();
        });
      }

      /* jump helper used by variant switching.
         FIX2 2026-07-04: takes the variant's featured_image OBJECT and jumps
         by image position (1-based -> slide index). The old src*=id match
         never worked — numeric image ids don't appear in CDN URLs. Uses the
         owned animation so scroll-snap can't cancel the jump. */
      /* colorway gallery filter — slides carry data-color (liquid, alt-based);
         untagged slides show for every color. Zoom modal filtered in lockstep. */
      window.__skpdpFilterColor = function (color) {
        var c = (color || '').toLowerCase();
        var shown = 0;
        Array.prototype.forEach.call(slides, function (s) {
          var sc = s.getAttribute('data-color');
          var show = !c || !sc || sc === c;
          s.style.display = show ? '' : 'none';
          if (show) shown++;
        });
        if (!shown) Array.prototype.forEach.call(slides, function (s) { s.style.display = ''; });
        if (zoom) {
          Array.prototype.forEach.call(slides, function (s) {
            var z = zoom.querySelector('[data-zoom-index="' + s.getAttribute('data-slide-index') + '"]');
            if (z) z.style.display = s.style.display;
          });
        }
        cancelGalAnim();
        gallery.scrollLeft = 0;
      };

      window.__skpdpJumpToImage = function (featuredImage) {
        if (!featuredImage) return;
        var index = null;
        if (typeof featuredImage.position === 'number') {
          index = featuredImage.position - 1;
        } else if (featuredImage.src) {
          var file = String(featuredImage.src).split('?')[0].split('/').pop();
          var img = gallery.querySelector('.product-main-image[src*="' + file.split('.')[0] + '"]');
          if (img) {
            var slide = img.closest('.skpdp-slide');
            if (slide) index = parseInt(slide.getAttribute('data-slide-index'), 10);
          }
        }
        if (index != null && !isNaN(index)) {
          var slide = gallery.querySelector('.skpdp-slide[data-slide-index="' + index + '"]');
          var ord = slide ? visibleSlides().indexOf(slide) : -1;
          goToSlide(ord >= 0 ? ord : 0);
        }
      };
    }

    /* ================================================== 2. VARIANTS (SKIMS state machine) */
    var atb = document.querySelector('[data-skpdp-atb]');
    var atbLabel = document.querySelector('[data-skpdp-atb-label]');
    var variantInput = document.getElementById('product-variant-id');
    var sizeButtons = Array.prototype.slice.call(document.querySelectorAll('.skpdp-size-btn'));
    var swatches = Array.prototype.slice.call(document.querySelectorAll('.skpdp-swatch'));
    var hasSizeOption = !!(window.productOptions && window.productOptions.size && sizeButtons.length);

    var state = { color: null, size: null };
    var activeSwatch = document.querySelector('.skpdp-swatch.is-selected');
    if (activeSwatch) state.color = activeSwatch.dataset.color;

    /* conversion kit: modules registered here get called after every
       variant-state change (variant may be null, sizeChosen boolean) */
    var kitHooks = [];

    /* low-stock line — "Only X left" when the resolved variant tracks
       inventory and sits within [1, threshold] */
    var kitStockEl = document.querySelector('[data-kit-stock]');
    var kitStockCount = document.querySelector('[data-kit-stock-count]');
    var kitStockThreshold = kitStockEl ? parseInt(kitStockEl.getAttribute('data-kit-stock-threshold'), 10) || 5 : 5;
    function updateKitStock(variant, sizeChosen) {
      if (!kitStockEl) return;
      /* inventory comes from window.productInventory (Liquid-embedded) —
         the `| json` variant payload does not expose inventory fields */
      var inv = variant && window.productInventory ? window.productInventory[String(variant.id)] : null;
      var show = !!(sizeChosen && inv && inv.managed && typeof inv.qty === 'number' && inv.qty > 0 && inv.qty <= kitStockThreshold);
      kitStockEl.hidden = !show;
      if (show && kitStockCount) kitStockCount.textContent = inv.qty;
    }

    function findVariant() {
      if (!window.productVariants || !window.productOptions) return null;
      var opts = window.productOptions;
      return window.productVariants.find(function (v) {
        var ok = true;
        if (opts.color && state.color != null) ok = ok && v['option' + opts.color] === state.color;
        if (opts.size && state.size != null) ok = ok && v['option' + opts.size] === state.size;
        return ok;
      }) || null;
    }

    function refreshSizeAvailability() {
      if (!hasSizeOption) return;
      var opts = window.productOptions;
      sizeButtons.forEach(function (btn) {
        var match = window.productVariants.find(function (v) {
          var ok = v['option' + opts.size] === btn.dataset.size;
          if (opts.color && state.color != null) ok = ok && v['option' + opts.color] === state.color;
          return ok;
        });
        var available = !!(match && match.available);
        btn.classList.toggle('is-unavailable', !available);
        btn.disabled = !available;
      });
    }

    function updateUI() {
      var variant = findVariant();
      var sizeChosen = !hasSizeOption || state.size != null;

      if (variant) {
        if (variantInput) variantInput.value = variant.id;
        window.currentVariantId = variant.id;

        var priceStr = moneyFormat(variant.price);
        var priceEl = document.querySelector('[data-current-price]');
        if (priceEl) priceEl.textContent = priceStr;
        var compareEl = document.querySelector('[data-compare-price]');
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = moneyFormat(variant.compare_at_price);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
        var klarnaEl = document.querySelector('[data-klarna-amount]');
        if (klarnaEl) klarnaEl.textContent = moneyFormat(Math.round(variant.price / 3));

        var colorLabel = document.querySelector('[data-current-color]');
        if (colorLabel && window.productOptions.color) {
          colorLabel.textContent = variant['option' + window.productOptions.color] || '';
        }
        if (variant.featured_image && window.__skpdpJumpToImage) {
          window.__skpdpJumpToImage(variant.featured_image);
        }
      }

      updateKitStock(variant, sizeChosen);

      if (atb && atbLabel) {
        if (!sizeChosen) {
          atb.disabled = true;
          atb.classList.remove('is-ready');
          atbLabel.textContent = 'Select a Size';
        } else if (!variant || !variant.available) {
          atb.disabled = true;
          atb.classList.remove('is-ready');
          atbLabel.textContent = variant ? 'Sold Out' : 'Unavailable';
        } else {
          atb.disabled = false;
          atb.classList.add('is-ready');
          atbLabel.textContent = 'Add to Bag';
        }
      }

      kitHooks.forEach(function (fn) { fn(variant, sizeChosen); });
    }

    sizeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled || btn.classList.contains('is-unavailable')) return;
        sizeButtons.forEach(function (b) {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
        state.size = btn.dataset.size;
        updateUI();
      });
    });

    swatches.forEach(function (btn) {
      btn.addEventListener('click', function () {
        swatches.forEach(function (b) {
          b.classList.remove('is-selected');
          b.removeAttribute('aria-current');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-current', 'true');
        state.color = btn.dataset.color;
        if (window.__skpdpFilterColor) window.__skpdpFilterColor(state.color);
        if (window.__skpdpFilterSameColor) window.__skpdpFilterSameColor(state.color);
        refreshSizeAvailability();
        // if the chosen size just went unavailable in this color, unselect it (SKIMS-style rest state)
        if (state.size != null) {
          var chosen = sizeButtons.find(function (b) { return b.dataset.size === state.size; });
          if (chosen && chosen.classList.contains('is-unavailable')) {
            chosen.classList.remove('is-selected');
            chosen.setAttribute('aria-pressed', 'false');
            state.size = null;
          }
        }
        updateUI();
      });
    });

    refreshSizeAvailability();
    if (hasSizeOption) {
      if (window.__skpdpFilterColor && state.color) window.__skpdpFilterColor(state.color);
      updateUI(); // rest state: ATB disabled, "Select a Size"
    }

    /* ================================================== 3. TABS */
    var tabs = Array.prototype.slice.call(document.querySelectorAll('[data-skpdp-tab]'));
    var panels = Array.prototype.slice.call(document.querySelectorAll('[data-skpdp-panel]'));
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var key = tab.getAttribute('data-skpdp-tab');
        tabs.forEach(function (t) {
          var active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          p.hidden = p.getAttribute('data-skpdp-panel') !== key;
        });
        // center a partially visible tab by scrolling ONLY the bar. The old
        // scrollIntoView also panned the PAGE horizontally in Safari when the
        // tab sat flush with the viewport edge (announcement bar looked cut
        // off — Damian 2026-07-04); scrollIntoView is never safe here.
        var bar = tab.closest('.skpdp-tabs__bar');
        if (bar && bar.scrollWidth > bar.clientWidth + 1) {
          var target = tab.offsetLeft - (bar.clientWidth - tab.offsetWidth) / 2;
          bar.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
        }
      });
    });

    /* ================================================== 4. CART helpers */
    function addToCart(variantId, onDone) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      }).then(function (res) {
        if (!res.ok) throw new Error('add failed ' + res.status);
        return fetch('/cart.js');
      }).then(function (res) { return res.json(); }).then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        var after = function () {
          if (window.SadyCart && window.SadyCart.open) window.SadyCart.open();
          if (onDone) onDone(true);
        };
        if (window.SadyCart && window.SadyCart.refresh) {
          Promise.resolve(window.SadyCart.refresh()).then(after, after);
        } else {
          after();
        }
      }).catch(function (err) {
        console.error('CTL add to cart error:', err);
        if (onDone) onDone(false);
      });
    }

    /* ================================================== 5. COMPLETE THE LOOK */
    var ctl = document.querySelector('[data-skpdp-ctl]');
    if (ctl) {
      var trigger = ctl.querySelector('[data-ctl-trigger]');
      var triggerLabel = ctl.querySelector('[data-ctl-trigger-label]');
      var listbox = ctl.querySelector('[data-ctl-listbox]');
      var ctlAtc = ctl.querySelector('[data-ctl-atc]');
      var selectedVariantId = ctlAtc && !trigger ? ctlAtc.getAttribute('data-variant-id') : null;

      if (trigger && listbox) {
        trigger.addEventListener('click', function (e) {
          e.stopPropagation();
          var open = !listbox.hidden;
          listbox.hidden = open;
          trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        });
        document.addEventListener('click', function (e) {
          if (!listbox.hidden && !ctl.contains(e.target)) {
            listbox.hidden = true;
            trigger.setAttribute('aria-expanded', 'false');
          }
        });
        listbox.querySelectorAll('[data-ctl-option]').forEach(function (opt) {
          opt.addEventListener('click', function () {
            if (opt.disabled) return;
            listbox.querySelectorAll('[data-ctl-option]').forEach(function (o) {
              o.classList.remove('is-selected');
            });
            opt.classList.add('is-selected');
            selectedVariantId = opt.getAttribute('data-variant-id') || null;
            if (triggerLabel) triggerLabel.textContent = opt.textContent.trim();
            listbox.hidden = true;
            trigger.setAttribute('aria-expanded', 'false');
            if (ctlAtc) {
              var ready = !!selectedVariantId;
              ctlAtc.disabled = !ready;
              ctlAtc.classList.toggle('is-ready', ready);
            }
          });
        });
      }

      if (ctlAtc) {
        ctlAtc.addEventListener('click', function () {
          if (ctlAtc.disabled || !selectedVariantId) return;
          var original = ctlAtc.textContent;
          ctlAtc.textContent = 'Adding…';
          addToCart(selectedVariantId, function (ok) {
            ctlAtc.textContent = ok ? original : 'Error — try again';
            if (!ok) setTimeout(function () { ctlAtc.textContent = original; }, 2000);
          });
        });
      }
    }

    /* ================================================== 6. SIMILAR STYLES */
    var recs = document.querySelector('[data-skpdp-recs]');

    function initRecsCarousel(container) {
      var viewport = container.querySelector('[data-recs-viewport]');
      if (!viewport) return;
      /* Scuffers-style superscript count; static rows render it in liquid */
      var heading = container.querySelector('.skpdp-recs__heading');
      if (heading && !heading.querySelector('[data-recs-count]')) {
        var sup = document.createElement('sup');
        sup.setAttribute('data-recs-count', '');
        sup.textContent = container.querySelectorAll('.skpdp-recs__cell').length;
        heading.appendChild(document.createTextNode(' '));
        heading.appendChild(sup);
      }
      var prev = container.querySelector('[data-recs-prev]');
      var next = container.querySelector('[data-recs-next]');
      var pageEl = container.querySelector('[data-recs-page]');
      var pagesEl = container.querySelector('[data-recs-pages]');

      var update = function () {
        var pageW = viewport.clientWidth;
        if (!pageW) return;
        var pages = Math.max(1, Math.ceil(viewport.scrollWidth / pageW));
        var max = viewport.scrollWidth - pageW;
        var page = pages > 1 && max > 0
          ? 1 + Math.round((viewport.scrollLeft / max) * (pages - 1))
          : 1;
        if (pagesEl) pagesEl.textContent = pages;
        if (pageEl) pageEl.textContent = page;
        if (prev) prev.disabled = page <= 1;
        if (next) next.disabled = page >= pages;
      };
      viewport.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      if (prev) prev.addEventListener('click', function () {
        viewport.scrollBy({ left: -viewport.clientWidth, behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        viewport.scrollBy({ left: viewport.clientWidth, behavior: 'smooth' });
      });
      update();
    }

    if (recs) {
      if (recs.querySelector('[data-skpdp-recs-inner]')) {
        initRecsCarousel(recs);
      } else if (recs.dataset.url) {
        fetch(recs.dataset.url)
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var tmp = document.createElement('div');
            tmp.innerHTML = html;
            var inner = tmp.querySelector('[data-skpdp-recs-inner]');
            if (inner) {
              recs.appendChild(inner);
              initRecsCarousel(recs);
            }
          })
          .catch(function (err) { console.error('Recommendations fetch error:', err); });
      }
    }

    /* static rec rows (Shop the Look / Shop the Same Color / We Think You'd Like) */
    document.querySelectorAll('[data-skpdp-static-recs]').forEach(function (row) {
      initRecsCarousel(row);
    });

    /* Shop the Same Color — cells stamped data-colors (|piped|); show only the
       selected swatch's colorway, hide the whole row when nothing matches */
    var sameColorRow = document.querySelector('[data-same-color-row]');
    window.__skpdpFilterSameColor = function (color) {
      if (!sameColorRow) return;
      var c = (color || '').toLowerCase();
      var visCount = 0;
      sameColorRow.querySelectorAll('.skpdp-recs__cell').forEach(function (cell) {
        var tags = (cell.getAttribute('data-colors') || '').split('|').filter(Boolean);
        var show = !c || tags.indexOf(c) !== -1;
        cell.style.display = show ? '' : 'none';
        if (show) visCount++;
      });
      if (!visCount) {
        /* fallback (Damian 2026-07-04 "where is shop by color?"): selected
           colorway has no exact matches -> show everything that shares ANY of
           this product's colorways instead of hiding the whole row */
        sameColorRow.querySelectorAll('.skpdp-recs__cell').forEach(function (cell) {
          cell.style.display = '';
          visCount++;
        });
      }
      var count = sameColorRow.querySelector('[data-recs-count]');
      if (count) count.textContent = visCount;
      sameColorRow.style.display = visCount ? '' : 'none';
      var vp = sameColorRow.querySelector('[data-recs-viewport]');
      if (vp) { vp.scrollLeft = 0; vp.dispatchEvent(new Event('scroll')); }
    };
    var selSwatch = document.querySelector('.skpdp-swatch.is-selected');
    window.__skpdpFilterSameColor(selSwatch ? selSwatch.dataset.color : '');

    /* ================================================== 7. MOBILE STICKY ATC
       (conversion kit) — Scuffers dual-visibility pattern, verbatim logic from
       product-sticky-atc.spec.md: shown while the real ATB is below the fold
       (is-before-form) or scrolled past (is-after-form); force-hidden when the
       footer enters the viewport (top < innerHeight - 50). rAF-throttled. */
    var sticky = document.querySelector('[data-kit-sticky]');
    if (sticky && atb) {
      var mqMobile = window.matchMedia('(max-width: 1023px)');
      var stickyCta = sticky.querySelector('[data-kit-sticky-cta]');
      var stickyCtaLabel = sticky.querySelector('[data-kit-sticky-cta-label]');
      var stickyPrice = sticky.querySelector('[data-kit-sticky-price]');
      var stickySizeBtn = sticky.querySelector('[data-kit-sticky-size]');
      var stickySizeValue = sticky.querySelector('[data-kit-sticky-size-value]');
      var sheet = document.querySelector('[data-kit-sheet]');
      var sheetSizes = sheet ? sheet.querySelector('[data-kit-sheet-sizes]') : null;
      var stickyBackdrop = document.querySelector('[data-kit-backdrop]');
      var kitFooter = document.querySelector('.schrome-footer') || document.querySelector('footer');

      function buildSheet() {
        if (!sheetSizes) return;
        sheetSizes.innerHTML = '';
        sizeButtons.forEach(function (real) {
          var li = document.createElement('li');
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'spdp-kit-sheet__size';
          b.textContent = real.dataset.size;
          if (real.classList.contains('is-selected')) b.classList.add('is-selected');
          if (real.classList.contains('is-unavailable')) {
            b.classList.add('is-unavailable');
            b.disabled = true;
          }
          b.addEventListener('click', function () {
            if (b.disabled) return;
            real.click();                       // real pill owns variant state
            buildSheet();                       // reflect selection instantly
            setTimeout(closeSheet, 200);        // spec: close after 200ms
          });
          li.appendChild(b);
          sheetSizes.appendChild(li);
        });
      }

      function openSheet() {
        if (!sheet) return;
        buildSheet();
        sheet.style.bottom = sticky.offsetHeight + 'px'; // sits directly above bar
        sheet.hidden = false;
        if (stickyBackdrop) stickyBackdrop.classList.add('is-active');
        if (stickySizeBtn) {
          stickySizeBtn.classList.add('is-open');
          stickySizeBtn.setAttribute('aria-expanded', 'true');
        }
      }

      function closeSheet() {
        if (!sheet || sheet.hidden) return;
        sheet.hidden = true;
        if (stickyBackdrop) stickyBackdrop.classList.remove('is-active');
        if (stickySizeBtn) {
          stickySizeBtn.classList.remove('is-open');
          stickySizeBtn.setAttribute('aria-expanded', 'false');
        }
      }

      var visRaf = null;
      function stickyVisibility() {
        if (!mqMobile.matches) {
          sticky.classList.add('is-hidden');
          return;
        }
        var r = atb.getBoundingClientRect();
        var isBefore = r.top > window.innerHeight;
        var isAfter = r.bottom < 0;
        var footerVisible = kitFooter
          ? kitFooter.getBoundingClientRect().top < window.innerHeight - 50
          : false;
        var show = (isBefore || isAfter) && !footerVisible;
        sticky.classList.toggle('is-before-form', isBefore && show);
        sticky.classList.toggle('is-after-form', isAfter && show);
        sticky.classList.toggle('is-hidden', !show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (!show) closeSheet();
      }
      window.addEventListener('scroll', function () {
        if (visRaf) return;
        visRaf = requestAnimationFrame(function () { visRaf = null; stickyVisibility(); });
      }, { passive: true });
      window.addEventListener('resize', function () {
        if (visRaf) return;
        visRaf = requestAnimationFrame(function () { visRaf = null; stickyVisibility(); });
      });

      if (stickySizeBtn && sheet) {
        stickySizeBtn.addEventListener('click', function () {
          if (sheet.hidden) openSheet(); else closeSheet();
        });
      }
      if (stickyBackdrop) stickyBackdrop.addEventListener('click', closeSheet);

      if (stickyCta) {
        stickyCta.addEventListener('click', function () {
          if (!stickyCta.classList.contains('is-ready')) return;
          closeSheet();
          atb.click(); // real submit — theme.js AJAX handler + cart drawer
        });
      }

      /* mirror real ATB state + price + selected size on every variant change */
      function stickySync() {
        if (stickyCtaLabel && atbLabel) stickyCtaLabel.textContent = atbLabel.textContent;
        if (stickyCta) {
          stickyCta.disabled = atb.disabled;
          stickyCta.classList.toggle('is-ready', atb.classList.contains('is-ready'));
        }
        if (stickyPrice) {
          var p = document.querySelector('[data-current-price]');
          if (p) stickyPrice.textContent = p.textContent;
        }
        if (stickySizeValue) stickySizeValue.textContent = state.size != null ? state.size : 'Select';
        if (sheet && !sheet.hidden) buildSheet();
      }
      kitHooks.push(stickySync);
      stickySync();
      stickyVisibility();
    }
  });
})();
